import React, { useState, useEffect } from 'react';
import { entryAPI, exportAPI } from '../services/api';
import { handleApiError } from '../utils/errorHandler';

const Entries = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
  });
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 10;

  useEffect(() => {
    fetchEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const response = await entryAPI.getAll(filters);
      setEntries(response.data);
    } catch (error) {
      handleApiError(error, 'Error fetching entries');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const handleExport = async (format) => {
    try {
      if (format === 'csv') {
        const response = await exportAPI.csv(filters);
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'ibd_entries.csv';
        a.click();
        window.URL.revokeObjectURL(url);
      } else if (format === 'pdf') {
        const response = await exportAPI.pdf(filters);
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'ibd_entries.pdf';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      handleApiError(error, 'Error exporting data');
      alert('Failed to export data. Please try again.');
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (window.confirm('Are you sure you want to delete this entry?')) {
      try {
        await entryAPI.delete(entryId);
        fetchEntries();
        setIsModalOpen(false);
      } catch (error) {
        handleApiError(error, 'Error deleting entry');
        alert('Failed to delete entry. Please try again.');
      }
    }
  };

  const openModal = (entry) => {
    setSelectedEntry(entry);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedEntry(null);
    setIsModalOpen(false);
  };

  // Pagination
  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = entries.slice(indexOfFirstEntry, indexOfLastEntry);
  const totalPages = Math.ceil(entries.length / entriesPerPage);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getSymptomColor = (level) => {
    if (!level) return 'bg-secondary-100 text-secondary-800';
    if (level <= 3) return 'bg-green-100 text-green-800';
    if (level <= 6) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-secondary-900 mb-2">Your Entries</h1>
        <p className="text-secondary-600">View and manage your health entries.</p>
      </div>

      {/* Filters and Export */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                name="start_date"
                value={filters.start_date}
                onChange={handleFilterChange}
                className="px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                name="end_date"
                value={filters.end_date}
                onChange={handleFilterChange}
                className="px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={() => setFilters({ start_date: '', end_date: '' })}
                className="px-4 py-2 text-secondary-600 hover:text-secondary-800 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => handleExport('csv')}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              Export CSV
            </button>
            <button
              onClick={() => handleExport('pdf')}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
            >
              Export PDF
            </button>
          </div>
        </div>
      </div>

      {/* Entries Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : currentEntries.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-secondary-200">
                <thead className="bg-secondary-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Pain
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Bloating
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Fatigue
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      BM
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-secondary-200">
                  {currentEntries.map((entry) => (
                    <tr key={entry.id} className="hover:bg-secondary-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-secondary-900">
                          {formatDate(entry.date)}
                        </div>
                        <div className="text-sm text-secondary-500">
                          {entry.time}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSymptomColor(entry.pain_level)}`}>
                          {entry.pain_level || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSymptomColor(entry.bloating)}`}>
                          {entry.bloating || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSymptomColor(entry.fatigue)}`}>
                          {entry.fatigue || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-secondary-900">
                          {entry.bm_consistency || 'N/A'}
                        </div>
                        {entry.bm_blood && (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                            Blood
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => openModal(entry)}
                          className="text-primary-600 hover:text-primary-900 mr-4"
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleDeleteEntry(entry.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 border-t border-secondary-200 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <p className="text-sm text-secondary-700">
                      Showing {indexOfFirstEntry + 1} to {Math.min(indexOfLastEntry, entries.length)} of {entries.length} entries
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm bg-white border border-secondary-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-secondary-50"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-1 text-sm text-secondary-700">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm bg-white border border-secondary-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-secondary-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📝</div>
            <h3 className="text-lg font-medium text-secondary-900 mb-2">No entries found</h3>
            <p className="text-secondary-500">Start logging your symptoms to see them here.</p>
          </div>
        )}
      </div>

      {/* Entry Detail Modal */}
      {isModalOpen && selectedEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-secondary-900">
                  Entry Details - {formatDate(selectedEntry.date)}
                </h2>
                <button
                  onClick={closeModal}
                  className="text-secondary-400 hover:text-secondary-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-6">
                {/* Date and Time */}
                <div>
                  <h3 className="text-lg font-semibold text-secondary-900 mb-2">📅 Date & Time</h3>
                  <p className="text-secondary-700">{formatDate(selectedEntry.date)} at {selectedEntry.time}</p>
                </div>

                {/* Meals */}
                {(selectedEntry.meal_food || selectedEntry.meal_notes) && (
                  <div>
                    <h3 className="text-lg font-semibold text-secondary-900 mb-2">🍽️ Meal Information</h3>
                    {selectedEntry.meal_food && (
                      <div className="mb-2">
                        <p className="font-medium text-secondary-700">Food/Drinks:</p>
                        <p className="text-secondary-600">{selectedEntry.meal_food}</p>
                      </div>
                    )}
                    {selectedEntry.meal_notes && (
                      <div>
                        <p className="font-medium text-secondary-700">Notes:</p>
                        <p className="text-secondary-600">{selectedEntry.meal_notes}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Symptoms */}
                <div>
                  <h3 className="text-lg font-semibold text-secondary-900 mb-2">😷 Symptoms</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="font-medium text-secondary-700">Pain Level:</p>
                      <span className={`inline-flex px-2 py-1 text-sm font-semibold rounded-full ${getSymptomColor(selectedEntry.pain_level)}`}>
                        {selectedEntry.pain_level || 'N/A'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-secondary-700">Bloating:</p>
                      <span className={`inline-flex px-2 py-1 text-sm font-semibold rounded-full ${getSymptomColor(selectedEntry.bloating)}`}>
                        {selectedEntry.bloating || 'N/A'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-secondary-700">Fatigue:</p>
                      <span className={`inline-flex px-2 py-1 text-sm font-semibold rounded-full ${getSymptomColor(selectedEntry.fatigue)}`}>
                        {selectedEntry.fatigue || 'N/A'}
                      </span>
                    </div>
                  </div>
                  {selectedEntry.other_symptoms && (
                    <div className="mt-4">
                      <p className="font-medium text-secondary-700">Other Symptoms:</p>
                      <p className="text-secondary-600">{selectedEntry.other_symptoms}</p>
                    </div>
                  )}
                </div>

                {/* Bowel Movement */}
                <div>
                  <h3 className="text-lg font-semibold text-secondary-900 mb-2">🚽 Bowel Movement</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="font-medium text-secondary-700">Consistency:</p>
                      <p className="text-secondary-600">{selectedEntry.bm_consistency || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="font-medium text-secondary-700">Urgency:</p>
                      <p className="text-secondary-600">{selectedEntry.bm_urgency || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="font-medium text-secondary-700">Pain:</p>
                      <p className="text-secondary-600">{selectedEntry.bm_pain || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="font-medium text-secondary-700">Blood:</p>
                      <p className="text-secondary-600">{selectedEntry.bm_blood ? 'Yes' : 'No'}</p>
                    </div>
                  </div>
                  {selectedEntry.bm_notes && (
                    <div className="mt-4">
                      <p className="font-medium text-secondary-700">Notes:</p>
                      <p className="text-secondary-600">{selectedEntry.bm_notes}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Entries;